<template>
  <div>
    <div v-if="loading" class="flex flex-col items-center justify-center py-24 text-slate-400">
      <div class="h-8 w-8 rounded-full border-2 border-slate-200 border-t-brand animate-spin"></div>
      <p class="mt-3 text-sm">Yükleniyor…</p>
    </div>

    <div v-else-if="notFound" class="text-center py-24">
      <div class="text-5xl mb-3">🔍</div>
      <p class="text-slate-500">Geçersiz veya süresi dolmuş bağlantı.</p>
    </div>

    <template v-else>
      <!-- Başlık + stepper -->
      <div class="rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <div class="flex items-center gap-3">
          <span class="rounded-2xl bg-brand/10 flex items-center justify-center text-xl" style="width:44px;height:44px;flex:none;">📣</span>
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium text-brand uppercase tracking-wide">Kampanya</p>
            <h1 class="text-lg font-bold text-slate-800 leading-tight">{{ data.campaignName }}</h1>
          </div>
        </div>

        <div class="mt-6 flex items-center">
          <template v-for="(s, i) in steps">
            <div :key="'d'+i" class="flex flex-col items-center" style="flex: 0 0 auto;">
              <div :class="['h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold transition', stepIndex > i ? 'bg-brand text-white' : stepIndex === i ? 'bg-brand text-white ring-4 ring-brand/15' : 'bg-slate-100 text-slate-400']">
                <span v-if="stepIndex > i">✓</span>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span :class="['mt-1.5 text-[11px] font-medium text-center', stepIndex >= i ? 'text-slate-700' : 'text-slate-400']">{{ s }}</span>
            </div>
            <div v-if="i < steps.length - 1" :key="'l'+i" :class="['h-1 rounded-full mx-1 sm:mx-2 -mt-5 flex-1 transition', stepIndex > i ? 'bg-brand' : 'bg-slate-100']"></div>
          </template>
        </div>
      </div>

      <!-- Brief -->
      <section v-if="data.briefText" class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Brief</p>
        <div class="brief-content text-slate-600 leading-relaxed" v-html="data.briefText"></div>
      </section>

      <!-- Aktif aksiyon kartı -->
      <section class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <!-- Revizyon -->
        <div v-if="data.status === 2" class="space-y-5">
          <div class="rounded-2xl bg-amber-50 border border-amber-200 p-4">
            <div class="flex items-center gap-2 font-semibold text-amber-800"><span>✏️</span> Revizyon talebi</div>
            <p class="text-sm text-amber-700 mt-1.5 whitespace-pre-line leading-relaxed">{{ data.revisionNote }}</p>
          </div>
          <p class="text-sm text-slate-600">Lütfen güncellenmiş içeriğini yeniden yükle.</p>
          <div v-if="data.draftUrlLabel">
            <label class="block text-sm font-medium text-slate-700 mb-1.5">{{ data.draftUrlLabel }}</label>
            <input v-model="draftUrl" type="url" :placeholder="data.draftUrlLabel" class="w-full rounded-xl border border-slate-200 py-2.5 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
          </div>
          <file-upload label="Yeni içerik (resim / video)" accept="image/*,video/*" :busy="busy" :disabled="!!data.draftUrlLabel && !draftUrl" @submit="(f) => upload(0, f)" />
        </div>

        <!-- İnceleniyor -->
        <div v-else-if="data.status === 0 || data.status === 1" class="text-center py-10">
          <div class="mx-auto h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center text-3xl">⏳</div>
          <p class="font-semibold text-slate-800 mt-4 text-lg">İçeriğin inceleniyor</p>
          <p class="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">Ekibimiz içeriğini değerlendiriyor. Onaylandığında bu sayfadan paylaşım adımına geçeceksin.</p>
        </div>

        <!-- Onaylandı → yayın -->
        <div v-else-if="data.status === 3" class="space-y-5">
          <div class="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
            <div class="flex items-center gap-2 font-semibold text-emerald-800"><span>🎉</span> İçeriğin onaylandı!</div>
            <p class="text-sm text-emerald-700 mt-1">Aşağıdaki bilgileri kullanarak paylaşımını yap.</p>
          </div>

          <!-- Kişiye özel paylaşım linki (Excel ile yüklenir) -->
          <div v-if="data.shareUrl" class="rounded-2xl border border-slate-200 p-4">
            <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Paylaşım linkin</p>
            <p class="text-sm text-slate-800 mt-1.5 break-all">{{ data.shareUrl }}</p>
            <p class="text-xs text-slate-500 mt-1.5">Bu link sana özeldir; paylaşımında bu linki kullan.</p>
            <button @click="copyShareUrl" class="mt-3 w-full rounded-xl border border-brand text-brand font-semibold py-2.5 transition hover:bg-brand/5">
              {{ shareCopied ? "Kopyalandı ✓" : "Linki kopyala" }}
            </button>
          </div>

          <div v-if="data.publishDate" class="rounded-2xl bg-brand/5 border border-brand/20 p-5 text-center">
            <p class="text-xs font-semibold text-brand uppercase tracking-wide">Yayınlanma Tarihi</p>
            <p class="text-2xl sm:text-3xl font-extrabold text-slate-800 mt-1.5">{{ formatDate(data.publishDate) }}</p>
          </div>

          <div class="space-y-3 pt-1">
            <p class="text-sm text-slate-600">Paylaşımını yaptıktan sonra gönderi linkini gir.</p>
            <input v-model="postUrl" type="url" placeholder="Gönderi linki (https://…)" class="w-full rounded-xl border border-slate-200 py-2.5 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
            <button :disabled="!postUrl || busy" @click="submitPostUrl" class="w-full rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-40 text-white font-semibold py-3 transition shadow-sm shadow-brand/30">
              {{ busy ? "Gönderiliyor…" : "Gönder →" }}
            </button>
          </div>
        </div>

        <!-- Yayın yüklendi → istatistik -->
        <div v-else-if="data.status === 4" class="space-y-5">
          <div class="rounded-2xl bg-violet-50 border border-violet-200 p-4">
            <div class="flex items-center gap-2 font-semibold text-violet-800"><span>📊</span> Son adım: İstatistik</div>
            <p class="text-sm text-violet-700 mt-1">Story istatistiklerinin ekran görüntüsünü yükle ve süreci tamamla.</p>
          </div>

          <!-- 24 saat dolmadıysa geri sayım -->
          <div v-if="statsCountdownMs > 0" class="text-center py-8">
            <div class="mx-auto h-16 w-16 rounded-2xl bg-violet-50 flex items-center justify-center text-3xl">⏱️</div>
            <p class="font-semibold text-slate-800 mt-4 text-lg">İstatistik yüklemeye az kaldı</p>
            <p class="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">Paylaşımının üzerinden 24 saat geçince story istatistiklerini yükleyebilirsin.</p>
            <div class="mt-4 inline-flex items-center gap-2 text-3xl sm:text-4xl font-extrabold text-violet-700 tabular-nums">
              {{ statsCountdownText }}
            </div>
          </div>

          <!-- 24 saat dolduysa yükleme + uyarı -->
          <template v-else>
            <div class="rounded-2xl bg-amber-50 border border-amber-200 p-4">
              <div class="flex items-center gap-2 font-semibold text-amber-800"><span>⚠️</span> Dikkat</div>
              <p class="text-sm text-amber-700 mt-1.5 leading-relaxed">
                Profil istatistiğini değil, <b>story istatistiklerinin</b> ekran görüntüsünü yükle.
              </p>
            </div>
            <file-upload label="Story istatistik ekran görüntüleri" accept="image/*" multiple :busy="busy" @submit="(f) => uploadStats(f)" />
            <p class="text-xs text-slate-400 text-center">Birden fazla ekran görüntüsü yükleyebilirsin.</p>
          </template>
        </div>

        <!-- Tamamlandı -->
        <div v-else-if="data.status === 5" class="text-center py-8">
          <div class="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center text-3xl">✅</div>
          <p class="font-semibold text-slate-800 mt-4 text-lg">Süreç tamamlandı!</p>
          <p class="text-sm text-slate-500 mt-1.5">Katkın için teşekkürler. 💜</p>

          <div class="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-left">
            <div class="flex items-center gap-2 font-semibold text-amber-800"><span>⏳</span> Ödeme bilgisi</div>
            <p class="text-sm text-amber-700 mt-1.5 leading-relaxed">
              Ödemen, sürecin tamamlanmasından itibaren <b>45 gün vade</b> ile yapılacaktır. Ödeme bilgilendirmesi e-posta ile gönderilecektir.
            </p>
          </div>

          <details class="mt-4 text-left">
            <summary class="text-sm text-brand font-medium cursor-pointer">Ek istatistik yükle</summary>
            <div class="mt-3">
              <file-upload label="İstatistik ekran görüntüleri" accept="image/*" multiple :busy="busy" @submit="(f) => uploadStats(f)" />
            </div>
          </details>
        </div>

        <p v-if="error" class="text-sm text-red-500 mt-4 text-center">{{ error }}</p>
      </section>

      <!-- Yüklenenler özeti -->
      <section v-if="data.submissions && data.submissions.length" class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
        <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Yüklediklerin</p>
        <div class="flex flex-wrap gap-3">
          <a v-for="s in data.submissions" :key="s.id" :href="s.fileUrl" target="_blank" class="group relative">
            <video v-if="isVideo(s.fileUrl)" :src="s.fileUrl" class="h-20 w-20 rounded-xl object-cover border border-slate-200 bg-black" muted></video>
            <img v-else :src="s.fileUrl" class="h-20 w-20 rounded-xl object-cover border border-slate-200" />
            <span class="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/55 rounded px-1 py-0.5 text-center">{{ typeText(s.type) }}</span>
          </a>
        </div>
      </section>

      <p v-if="copied" class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-sm px-4 py-2 rounded-full shadow-lg">Kopyalandı ✓</p>
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from "vue-property-decorator";
import api from "./api";
import FileUpload from "./file-upload.vue";

@Component({ components: { FileUpload } })
export default class InfluencerPortal extends Vue {
  loading = true;
  notFound = false;
  busy = false;
  error = "";
  copied = false;
  shareCopied = false;
  data: any = {};
  postUrl = "";
  draftUrl = "";
  now = Date.now();
  tick: any = null;

  steps = ["Yüklendi", "Onay", "Yayın", "İstatistik"];

  // Yayına geçtikten 24 saat sonra istatistik yükleme açılır.
  get statsCountdownMs(): number {
    if (!this.data.postSubmittedAt) return 0;
    const unlock = new Date(this.data.postSubmittedAt).getTime() + 24 * 60 * 60 * 1000;
    return Math.max(0, unlock - this.now);
  }

  get statsCountdownText(): string {
    let s = Math.floor(this.statsCountdownMs / 1000);
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); s -= m * 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  get token(): string {
    return this.$route.params.token;
  }

  get stepIndex(): number {
    const map: { [k: number]: number } = { 0: 0, 1: 0, 2: 0, 3: 1, 4: 2, 5: 3 };
    return map[this.data.status] ?? 0;
  }

  async mounted() {
    await this.load();
    this.tick = setInterval(() => (this.now = Date.now()), 1000);
  }

  beforeDestroy() {
    if (this.tick) clearInterval(this.tick);
  }

  async load() {
    this.loading = true;
    try {
      this.data = await api.getJson(`/api/ContentCampaigns/portal/${this.token}`);
      if (this.data.draftUrl) this.draftUrl = this.data.draftUrl;
    } catch {
      this.notFound = true;
    } finally {
      this.loading = false;
    }
  }

  isVideo(url: string): boolean {
    return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url || "");
  }

  typeText(t: number): string {
    return ["Taslak", "Yayın", "İstatistik"][t] || "";
  }

  formatDate(value: string): string {
    if (!value) return "";
    return new Date(value).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  }

  copy(text: string) {
    navigator.clipboard.writeText(text);
    this.copied = true;
    setTimeout(() => (this.copied = false), 1500);
  }

  copyShareUrl() {
    if (!this.data.shareUrl) return;
    navigator.clipboard.writeText(this.data.shareUrl);
    this.shareCopied = true;
    setTimeout(() => (this.shareCopied = false), 1500);
  }

  async uploadStats(files: File | File[]) {
    const list = Array.isArray(files) ? files : [files];
    if (!list.length) return;
    this.error = "";
    this.busy = true;
    try {
      const form = new FormData();
      form.append("type", "2"); // Stats
      // Tüm görseller tek istekte; backend her birini ayrı submission olarak kaydeder.
      for (const file of list) form.append("file", file);
      await api.postForm(`/api/ContentCampaigns/portal/${this.token}/submit`, form);
      await this.load();
    } catch {
      this.error = "Yükleme başarısız. Tekrar dene.";
    } finally {
      this.busy = false;
    }
  }

  async submitPostUrl() {
    if (!this.postUrl) return;
    this.error = "";
    this.busy = true;
    try {
      const form = new FormData();
      form.append("type", "1"); // Post
      form.append("postUrl", this.postUrl);
      await api.postForm(`/api/ContentCampaigns/portal/${this.token}/submit`, form);
      this.postUrl = "";
      await this.load();
    } catch {
      this.error = "Gönderilemedi. Tekrar dene.";
    } finally {
      this.busy = false;
    }
  }

  async upload(type: number, file: File) {
    this.error = "";
    this.busy = true;
    try {
      const form = new FormData();
      form.append("type", String(type));
      if (type === 1) form.append("postUrl", this.postUrl);
      if (type === 0 && this.draftUrl) form.append("draftUrl", this.draftUrl);
      form.append("file", file);
      await api.postForm(`/api/ContentCampaigns/portal/${this.token}/submit`, form);
      this.postUrl = "";
      await this.load();
    } catch {
      this.error = "Yükleme başarısız. Tekrar dene.";
    } finally {
      this.busy = false;
    }
  }
}
</script>

<style>
.brief-content { word-break: break-word; }
.brief-content h1 { font-size: 1.5rem; font-weight: 700; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content h2 { font-size: 1.25rem; font-weight: 700; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content p { margin: 0 0 0.6em; }
.brief-content ul { list-style: disc; padding-left: 1.4em; margin: 0 0 0.6em; }
.brief-content ol { list-style: decimal; padding-left: 1.4em; margin: 0 0 0.6em; }
.brief-content a { color: #6d28d9; text-decoration: underline; }
.brief-content strong { font-weight: 700; }
.brief-content u { text-decoration: underline; }
</style>
